/**
 * VENDORIQ-PORTAL-3 (2026-09-27): a vendor whose portal tab was opened BEFORE a
 * deploy navigates to a lazy route (Login / Register / Dashboard) and the browser
 * requests the old content-hashed chunk, which the deploy removed:
 * "TypeError: Failed to fetch dynamically imported module". The route error
 * boundary showed "Something went wrong" on the login/register page.
 *
 * `index.html` is already served no-cache, so ONE full reload fetches the new
 * build and the navigation succeeds. The reload is rate-limited through
 * sessionStorage so a genuinely missing chunk can never loop.
 */
const RELOAD_KEY = 'vendoriq:stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite CSS preload
  // VENDORIQ-PORTAL-6: our `vite:preloadError` handler calls preventDefault() while the
  // reload is scheduled, so Vite resolves the failed import with `undefined` and React.lazy
  // then reads `.default` of it before the reload lands.
  /can't access property "default", \w+\._result is undefined/i, // Firefox
  /Cannot read properties of undefined \(reading 'default'\)/i, // Chromium
  /undefined is not an object \(evaluating '\w+\._result\.default'\)/i, // Safari
];

export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: unknown }).name;
  if (name === 'ChunkLoadError') return true;
  const message =
    typeof err === 'string' ? err : String((err as { message?: unknown }).message ?? '');
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

interface ReloadDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  now: () => number;
}

function defaultDeps(): ReloadDeps {
  let storage: ReloadDeps['storage'] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return { storage, reload: () => window.location.reload(), now: () => Date.now() };
}

/** Reload once to pick up the new build. Returns false if we just did (no loop). */
export function reloadOnceForStaleChunk(deps: ReloadDeps = defaultDeps()): boolean {
  const { storage, reload, now } = deps;
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (last && now() - last < RELOAD_WINDOW_MS) return false;
    storage.setItem(RELOAD_KEY, String(now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Vite fires `vite:preloadError` when a dynamic import's chunk fails to load. */
export function installStaleChunkReload(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}
